import { useEffect, useRef, useState, type ReactElement } from 'react';

import { ApiError, api } from '@/api/client';
import { viteEnv } from '@/api/env';
import { loadGoogleScript } from '@/auth/google';
import { useSession } from '@/auth/useSession';

type Failure =
  | { kind: 'not_invited'; message: string }
  | { kind: 'retryable'; message: string }
  | null;

/**
 * The whole front door.
 *
 * Rowan will meet this screen on a phone, once, having clicked a link. It has
 * one job and one control, and every failure it can show says what to do next
 * rather than what went wrong internally.
 */
export function SignIn(): ReactElement {
  const { refresh } = useSession();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    const alive = { current: true };
    const clientId = viteEnv().VITE_GOOGLE_CLIENT_ID ?? '';

    const exchange = (credential: string): void => {
      setFailure(null);
      setBusy(true);
      api.auth
        .google(credential)
        // The exchange sets the cookie; who the user is comes from re-reading
        // the session, so there is one source of truth for it.
        .then(() => refresh())
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'not_invited') {
            setFailure({ kind: 'not_invited', message: err.message });
            return;
          }
          setFailure({
            kind: 'retryable',
            message:
              err instanceof ApiError ? err.message : 'Could not reach Ugin. Check your connection.',
          });
        })
        .finally(() => {
          if (alive.current) setBusy(false);
        });
    };

    void (async () => {
      // Checked before the script is fetched: with no client id there is
      // nothing the script could do for us.
      if (clientId === '') {
        setConfigured(false);
        return;
      }
      const google = await loadGoogleScript();
      if (!alive.current) return;
      if (google === null) {
        setConfigured(false);
        return;
      }
      setConfigured(true);
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => { exchange(response.credential); },
      });
      const slot: HTMLDivElement | null = buttonRef.current;
      if (slot !== null) {
        google.accounts.id.renderButton(slot, {
          theme: 'filled_black',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
        });
      }
    })();

    return () => {
      alive.current = false;
    };
  }, [refresh]);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-ugin-bg px-4 py-10">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-ugin-text">Sign in to Ugin</h1>
          <p className="text-sm text-ugin-text-muted">
            Ugin is invite-only. Use the Google account your invite was sent to.
          </p>
        </div>

        <div className="flex justify-center" ref={buttonRef} data-testid="google-button-slot" />

        {configured === false ? (
          <p className="text-sm text-ugin-text-muted">
            Sign-in is not configured on this instance yet.
          </p>
        ) : null}

        {busy ? (
          <p className="text-sm text-ugin-text-muted" role="status">
            Signing you in…
          </p>
        ) : null}

        {failure?.kind === 'not_invited' ? (
          <div className="space-y-1 rounded-md border border-ugin-border p-4 text-sm">
            <p className="text-ugin-text">{failure.message}</p>
            <p className="text-ugin-text-muted">
              Ask Eli to add this address, then sign in again.
            </p>
          </div>
        ) : null}

        {failure?.kind === 'retryable' ? (
          <div className="space-y-3 rounded-md border border-ugin-border p-4 text-sm">
            <p className="text-ugin-text">{failure.message}</p>
            <button
              type="button"
              className="min-h-11 w-full rounded-md border border-ugin-border px-4 py-2 text-ugin-text"
              onClick={() => { setFailure(null); }}
            >
              Try again
            </button>
          </div>
        ) : null}
      </div>
    </main>
  );
}
